import { useCallback, useState } from "react";
import { Link } from "react-router";
import { useDashboard } from "@/contexts/dashboard-context";
import { useTrackingActions } from "@/hooks/use-tracking-actions";
import { ShiftControl } from "./shift-control";
import { TodaySummary } from "./today-summary";
import { WeeklySnapshot } from "./weekly-snapshot";
import { CapacityMeter } from "./capacity-meter";
import { PullToRefresh } from "@/components/ui/pull-to-refresh";
import { Card, CardContent } from "@/components/ui/card";
import { EndRouteReminderDialog } from "@/components/shifts/end-route-reminder-dialog";
import { ShiftSummaryDialog } from "@/components/shifts/shift-summary-dialog";
import { Lightbulb } from "lucide-react";
import { Id } from "@convex/_generated/dataModel";

export function Dashboard() {
  const { rollingHours, showEndRouteReminder, dismissEndRouteReminder, activeShift } = useDashboard();
  const { stopCurrentTracking } = useTrackingActions();

  const [completedShiftId, setCompletedShiftId] = useState<Id<"shifts"> | null>(null);
  const [showSummary, setShowSummary] = useState(false);

  const handleRefresh = useCallback(async () => {
    // Convex data is real-time, so this is just for visual feedback
    await new Promise((resolve) => setTimeout(resolve, 500));
  }, []);

  const handleRouteComplete = useCallback(async (_endTime: string, _endLocation: { lat: number; lng: number }) => {
    // Store shift ID before ending (activeShift will become null)
    const shiftId = activeShift?._id;

    // End the shift using existing tracking actions
    const success = await stopCurrentTracking();

    if (success && shiftId) {
      dismissEndRouteReminder();
      setCompletedShiftId(shiftId);
      setShowSummary(true);
    }
  }, [activeShift, stopCurrentTracking, dismissEndRouteReminder]);

  const handleReminderDismiss = useCallback(() => {
    dismissEndRouteReminder();
  }, [dismissEndRouteReminder]);

  return (
    <>
      <PullToRefresh onRefresh={handleRefresh}>
        <div className="space-y-6 pb-24 pb-[calc(6rem+env(safe-area-inset-bottom))]">
          {/* Route Tracking */}
          <ShiftControl />

          {/* Today's Summary - Tap to see Daily view */}
          <TodaySummary />

          {/* Weekly Snapshot - Tap to see Weekly view (with expandable stats) */}
          <WeeklySnapshot />

          {/* Unified Capacity Display */}
          <CapacityMeter data={rollingHours} />

          {/* Insights Discovery Card */}
          <Link to="/insights">
            <Card className="hover:bg-accent/50 transition-colors cursor-pointer">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Lightbulb className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">Insights</h3>
                  <p className="text-sm text-muted-foreground">
                    View patterns and trends from your work history
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </PullToRefresh>

      {/* End Route Reminder Dialog */}
      <EndRouteReminderDialog
        open={showEndRouteReminder}
        onOpenChange={(open) => {
          if (!open) handleReminderDismiss();
        }}
        onComplete={handleRouteComplete}
      />

      {/* Shift Summary after completion */}
      <ShiftSummaryDialog
        shiftId={completedShiftId}
        open={showSummary}
        onOpenChange={setShowSummary}
      />
    </>
  );
}
